import { math } from '../math';
import { isValidNum } from '../number';
import { Color } from './color';
import {
	NumberToColorMapStepped,
	steppedYieldItem,
} from './number-to-color-map-stepped';


interface steppedWithLabelsYieldItem extends steppedYieldItem {
	label: string;
}

export class NumberToColorMapSteppedWithLabels extends NumberToColorMapStepped {
	public labels: string[];
	public constructor(
		positions: number[],
		colors: (Color | string)[],
		labels?: string[]
	) {
		super(positions, colors);

		this.labels =
			labels && labels.length == colors.length
				? labels
				: this.colors.map((c) => '');
	}

	public setLabel(i: number, label: string) {
		if (0 <= i && i < this.labels.length && this.labels[i] !== label) {
			this.labels[i] = label;
			return true;
		}
		return false;
	}

	public clone() {
		return <this>(
			new NumberToColorMapSteppedWithLabels(
				this.positions.slice(),
				this.colors,
				this.labels.slice()
			)
		);
	}

	public isValid() {
		if (!super.isValid()) return false;

		if (!Array.isArray(this.labels)) return false;
		if (this.positions.length !== this.labels.length + 1) return false;

		return true;
	}

	public clean() {
		const map = new Map<number, [Color, string]>();
		let q: number | undefined;
		for (const [i, p] of this.positions.entries()) {
			if (isValidNum(p) && (q === undefined || q <= p)) {
				map.set(p, [this.colors[i] ?? null, this.labels[i] ?? null]);
				if (!this.colors[i] || !this.labels[i]) break;
			}
			q = p;
		}

		const positions = [...map.keys()];
		const colors = [...map.values()].map((v) => v[0]).filter((v) => !!v);
		const labels = [...map.values()].map((v) => v[1]).filter((v) => !!v);
		return new NumberToColorMapSteppedWithLabels(positions, colors, labels);
	}

	public *entries() {
		for (let i = 1; i < this.positions.length; ++i)
			yield <steppedWithLabelsYieldItem>{
				a: this.positions[i - 1],
				b: this.positions[i],
				color: this.colors[i - 1],
				label: this.labels[i - 1],
			};
	}

	public [Symbol.iterator]() {
		return this.entries();
	}

	public delete(index: number) {
		if (0 <= index && index < this.positions.length) {
			this.positions.splice(index, 1);
			this.colors.splice(index, 1);
			this.labels.splice(index, 1);
			this.colors.length = this.positions.length - 1;
			this.labels.length = this.positions.length - 1;
			return true;
		}
		return false;
	}

	public slice(begin: number, end: number) {
		const positions: number[] = [];
		const colors: Color[] = [];
		const labels: string[] = [];
		for (let { a, b, color, label } of this) {
			a = math.max(begin, a);
			b = math.min(end, b);
			if (a < b) {
				if (positions.at(-1) !== a) positions.push(a);
				positions.push(b);
				colors.push(color);
				labels.push(label);
			}
		}
		return new NumberToColorMapSteppedWithLabels(positions, colors, labels);
	}

	public toJson() {
		return {
			positions: this.positions.map((v) => {
				if (v === -Infinity) return '-∞';
				if (v === +Infinity) return '+∞';
				return v;
			}),
			colors: this.colors.map((c) => c.toString()),
			labels: this.labels.map((c) => c ?? null),
		};
	}
}

export namespace NumberToColorMapSteppedWithLabels {
	export interface JSON {
		positions: (number | '-∞' | '+∞')[];
		colors: string[];
		labels?: string[];
	}
	export function fromJson(json: NumberToColorMapSteppedWithLabels.JSON) {
		if (!json) return null;
		const positions = json.positions.map((v) => {
			if (v === '-∞') return -Infinity;
			if (v === '+∞') return +Infinity;
			return v;
		});
		const colors = json.colors.map((c) => Color.html(c));
		const labels = json.labels
			? json.labels.map((l) => l ?? null)
			: colors.map((c) => '');
		return new NumberToColorMapSteppedWithLabels(positions, colors, labels);
	}
	// NumberToColorMap.fromJSONExtension.push(fromJson);
}
